import { Head, router, usePage } from '@inertiajs/react'
import { useState } from 'react'
import type { OfferCard, OffersIndexProps } from '@/types/offers'
import type { SharedPageProps } from '@/types/shared'
import { CandidateNav } from '@/components/Shared/CandidateNav'
import { FlashBanner } from '@/components/Shared/FlashBanner'

function movedOn(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function OfferedCardActions({ card }: { card: OfferCard }) {
  const [confirming, setConfirming] = useState<'accept' | 'decline' | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = () => {
    if (!confirming) {
      return
    }
    router.post(`/offers/${card.id}/${confirming}`, {}, {
      onStart: () => setBusy(true),
      onFinish: () => {
        setBusy(false)
        setConfirming(null)
      },
    })
  }

  if (confirming) {
    return (
      <div className="mt-3">
        <p className="mb-2 font-sans text-[13px] font-semibold text-foreground">
          {confirming === 'accept' ? 'Accept this offer?' : 'Decline this offer?'}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-[34px] rounded-[9px] bg-primary px-3 font-sans text-[13px] font-semibold text-primary-foreground disabled:opacity-60"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setConfirming(null)}
            disabled={busy}
            className="h-[34px] rounded-[9px] border border-border px-3 font-sans text-[13px] font-semibold text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-3 flex gap-2">
      <button
        type="button"
        onClick={() => setConfirming('accept')}
        className="h-[34px] rounded-[9px] bg-primary px-3 font-sans text-[13px] font-semibold text-primary-foreground"
      >
        Accept
      </button>
      <button
        type="button"
        onClick={() => setConfirming('decline')}
        className="h-[34px] rounded-[9px] border border-border px-3 font-sans text-[13px] font-semibold text-foreground"
      >
        Decline
      </button>
    </div>
  )
}

export default function OffersIndex({ lanes }: OffersIndexProps) {
  const { flash } = usePage<SharedPageProps>().props
  const boardEmpty = lanes.every((lane) => lane.applications.length === 0)

  return (
    <>
      <Head title="My offers — Hyrin" />

      <div className="min-h-screen bg-muted">
        <CandidateNav />
        <FlashBanner flash={flash} />

        <main className="mx-auto max-w-[1320px] px-6 py-12">
          <h1 className="mb-6 font-heading text-[32px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
            My offers
          </h1>

          {boardEmpty ? (
            <div className="max-w-[720px] rounded-[14px] border border-border bg-card p-[22px]">
              <p className="mb-1 font-heading text-[18px] font-bold text-foreground">No offers yet</p>
              <p className="font-sans text-[14px] leading-[1.45] text-muted-foreground">
                When a company makes you an offer, it lands here.
              </p>
            </div>
          ) : (
            <div className="-mx-6 overflow-x-auto px-6 pb-3">
              <div className="flex items-stretch gap-[14px]">
                {lanes.map((lane) => (
                  <section
                    key={lane.key}
                    className="flex w-[300px] shrink-0 flex-col rounded-card bg-lane p-[10px]"
                  >
                    <p className="mb-3 px-[6px] pt-[4px] font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
                      {lane.label} · {lane.applications.length}
                    </p>
                    <div className="flex flex-col gap-[10px]">
                      {lane.applications.map((card) => (
                        <div key={card.id} className="rounded-card border border-border bg-card p-[16px] shadow-sm">
                          <div className="flex items-center gap-2">
                            <p className="font-sans text-[15px] font-semibold leading-[1.35] text-foreground">
                              {card.positionTitle}
                            </p>
                            {lane.key === 'offered' && card.hasUnreadOffer && (
                              <span role="img" aria-label="Unread offer" className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                            )}
                          </div>
                          <p className="mt-[3px] font-sans text-[13px] text-muted-foreground">
                            {card.companyName}
                          </p>
                          <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                            moved {movedOn(card.movedAt)}
                          </p>
                          {lane.key === 'offered' && <OfferedCardActions card={card} />}
                        </div>
                      ))}
                      {lane.applications.length === 0 && (
                        <p className="px-[6px] py-[10px] font-sans text-[13px] text-muted-foreground">
                          Nothing here yet
                        </p>
                      )}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  )
}
